import * as React from "react";
import { useState, useCallback } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import {
  Link,
  DataTable,
  NoSearchResult,
  PaginatedList,
  QueryHelper,
  SearchForm,
  HoverForHelp
} from "components/common";
import useShowRouteForEntity from "routing/hooks/useShowRouteForEntity";
import useSearchFilterUsagesQuery from "search-filter/hooks/useSearchFilterUsagesQuery";
import { FILTER_USAGE_ENTITY_TYPE } from "search-filter/constants";
import Routes from "routing/Routes";
import usePermissions from "hooks/usePermissions";
export const DEFAULT_PAGINATION = {
  per_page: 25,
  page: 1,
  count: 0,
  total: 0
};
const DEFAULT_QUERY = {
  query: "",
  per_page: 10,
  page: 1,
  sort: "title",
  order: "asc"
};
const StyledPaginatedList = styled(PaginatedList)`
  .pagination {
    margin: 0;
  }
`;
const Title = styled.div`
  display: flex;
  align-items: center;
`;
const StyledHoverForHelp = styled((props) => /* @__PURE__ */ React.createElement(HoverForHelp, { ...props }))`
  margin-left: 5px;
`;
const FilterUsagesInDashboardWidget = ({
  id,
  title,
  widget_title: widgetTitle,
  owner,
  capability,
  query_id,
  widget_id
}) => {
  const { isPermitted } = usePermissions();
  const { t } = useTranslation("enterprise_search");
  const hasViewPermissions = isPermitted(`view:read:${id}`);
  const actualWidgetTitle = widgetTitle || t("filter_usages_in_dashboard_widgets.no_widget_title");
  const entityRoute = useShowRouteForEntity(id, "DASHBOARD");
  return /* @__PURE__ */ React.createElement("tr", { key: `${id}-${widget_id}` }, /* @__PURE__ */ React.createElement("td", { className: "limited" }, /* @__PURE__ */ React.createElement(Title, null, hasViewPermissions ? /* @__PURE__ */ React.createElement(Link, { to: entityRoute }, title) : title, capability === "None" && /* @__PURE__ */ React.createElement(StyledHoverForHelp, { title: t("filter_usages_in_dashboard_widgets.missing_permissions") }, t("filter_usages_in_dashboard_widgets.missing_permissions_dashboard")))), /* @__PURE__ */ React.createElement("td", null, /* @__PURE__ */ React.createElement(Title, null, hasViewPermissions ? /* @__PURE__ */ React.createElement(Link, { to: `${Routes.dashboard_show(id)}?page=${query_id}&focusedId=${widget_id}&editing=true` }, actualWidgetTitle) : actualWidgetTitle, capability === "None" && /* @__PURE__ */ React.createElement(StyledHoverForHelp, { title: t("filter_usages_in_dashboard_widgets.missing_permissions") }, t("filter_usages_in_dashboard_widgets.missing_permissions_widget")))), /* @__PURE__ */ React.createElement("td", null, owner), /* @__PURE__ */ React.createElement("td", null, capability));
};
const FilterUsagesInDashboardWidgets = ({ filterId }) => {
  const [filterQuery, setSearchFilter] = useState(DEFAULT_QUERY);
  const { data } = useSearchFilterUsagesQuery(filterId, {
    ...filterQuery,
    type: FILTER_USAGE_ENTITY_TYPE.DASHBOARD_WIDGET
  });
  const { t } = useTranslation("enterprise_search");
  const headers = [
    t("filter_usages_in_dashboard_widgets.dashboard_name"),
    t("filter_usages_in_dashboard_widgets.widget_name"),
    t("filter_usages_in_dashboard_widgets.owner_creator"),
    t("filter_usages_in_dashboard_widgets.capability")
  ];
  const handlePaginationChange = useCallback(
    (page) => {
      setSearchFilter((cur) => ({ ...cur, page }));
    },
    [setSearchFilter]
  );
  const handleSearch = useCallback(
    (newSearchQuery) => setSearchFilter((cur) => ({
      ...cur,
      page: DEFAULT_QUERY.page,
      query: newSearchQuery
    })),
    []
  );
  const handleReset = useCallback(() => handleSearch(DEFAULT_QUERY.query), [handleSearch]);
  const renderOverviewItem = useCallback(
    (entity) => /* @__PURE__ */ React.createElement(FilterUsagesInDashboardWidget, { ...entity }),
    []
  );
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("p", { className: "description" }, t("filter_usages_in_dashboard_widgets.found_widgets", { count: data.pagination.total })), /* @__PURE__ */ React.createElement(
    StyledPaginatedList,
    {
      activePage: filterQuery.page,
      totalItems: data.pagination.total,
      onChange: handlePaginationChange
    },
    /* @__PURE__ */ React.createElement(
      DataTable,
      {
        customFilter: /* @__PURE__ */ React.createElement(
          SearchForm,
          {
            onSearch: handleSearch,
            queryHelpComponent: /* @__PURE__ */ React.createElement(QueryHelper, { entityName: "dashboard", commonFields: ["id"] }),
            onReset: handleReset,
            topMargin: 0
          }
        ),
        dataRowFormatter: renderOverviewItem,
        noDataText: /* @__PURE__ */ React.createElement(NoSearchResult, null, t("filter_usages_in_dashboard_widgets.no_usages")),
        headers,
        id: "shared-entities",
        rowClassName: "no-bm",
        rows: data.elements,
        sortByKey: "title"
      }
    )
  ));
};
export default FilterUsagesInDashboardWidgets;
